import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import { useGuestContext } from '../context/GuestContext';
import { searchGuestTopics, type GuestTopicSearchResultDTO } from '../services/guestApi';
import { trackGuestEvent } from '../services/guestAnalyticsService';

/**
 * Guest topic search – Option 1. Text search + single-topic link.
 */
const GuestSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { ensureSession, guestId, isReady, error: sessionError } = useGuestContext();
  const [q, setQ] = useState('');
  const [submittedQ, setSubmittedQ] = useState<string | null>(null);
  const [results, setResults] = useState<GuestTopicSearchResultDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const runSearch = useCallback(
    async (query: string | null) => {
      setLoading(true);
      setSearchError(null);
      try {
        const list = await searchGuestTopics({ q: query ?? undefined });
        setResults(list);
        setSubmittedQ(query);
        const gid = guestId ?? localStorage.getItem('guestId');
        trackGuestEvent(gid, {
          eventType: 'search_query',
          entryMethod: 'SEARCH',
          metadata: { query_length: (query ?? '').length, result_count: list.length },
        });
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Search failed';
        setSearchError(message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    ensureSession()
      .then((gid) => trackGuestEvent(gid, { eventType: 'path_selected', entryMethod: 'SEARCH' }))
      .catch(() => {});
  }, [ensureSession]);

  // No search on page load – only when user submits (avoids unnecessary API call and 400 on load)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(q.trim() || null);
  };

  const handleTopicClick = (topicId: number) => {
    const gid = guestId ?? localStorage.getItem('guestId');
    trackGuestEvent(gid, {
      eventType: 'search_result_clicked',
      entryMethod: 'SEARCH',
      topicId,
    });
    navigate(`/guest/topic/${topicId}`);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/guest/available')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Search and try a topic
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Search by topic name or description, or browse suggested topics below.
        </Typography>

        {sessionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sessionError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search topics..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={24} />
                </InputAdornment>
              ) : null,
            }}
          />
          <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading}>
            Search
          </Button>
        </Box>

        {searchError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {searchError}
          </Alert>
        )}

        {submittedQ !== null && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {submittedQ !== '' ? `Results for "${submittedQ}"` : 'Suggested topics'}
          </Typography>
        )}
        {submittedQ !== null && results.length === 0 && !loading && (
          <Typography color="text.secondary">No topics found. Try a different search.</Typography>
        )}
        {submittedQ === null && !loading && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Enter a topic name or description above and click Search to find topics.
          </Typography>
        )}
        <List disablePadding>
          {results.map((r) => (
            <ListItemButton
              key={r.topicId}
              onClick={() => handleTopicClick(r.topicId)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary={r.title}
                secondary={r.courseName ? `Course: ${r.courseName}` : r.description}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate('/guest/available')}>
            Back to Try as Guest
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestSearchPage;
